import { Injectable } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

import { TransfersShareDialogComponent } from "src/app/components/dialogs/audiences/transfers-share-dialog/transfers-share-dialog.component";

import {
  Audience,
  Transfer
} from "src/app/models/services/audience";

import {
  ShareAudience,
  TransferShareAudience,
  TransferShareBusiness,
  TransferShareItem,
  TransferSharePlatform,
  TransferShareSavedGroup,
  UpdateSharedAudienceRequest
} from "src/app/models/services/transfer-share-audience.model";

interface IncomingTransferShareAudience {
  transferId?: string | number;
  platformId?: string | number;
  platforms?: Partial<TransferSharePlatform>[];
  businesses?: Partial<TransferShareBusiness>[];
  groups?: Partial<TransferShareSavedGroup>[];
}

@Injectable({
  providedIn: "root"
})
export class TransfersShareDialogService {
  lastErrorCloseResult: string;

  constructor(private modalService: NgbModal) {}

  public show(
    audience: Audience,
    transfer: Transfer,
    shareAudience: TransferShareAudience,
    acceptCallback: (request: UpdateSharedAudienceRequest) => void,
    rejectCallback?: () => void
  ): void;

  public show(
    audience: Audience,
    transfer: Transfer,
    shareAudience: TransferShareAudience,
    sharedAudience: TransferShareAudience | ShareAudience[] | null,
    acceptCallback: (request: UpdateSharedAudienceRequest) => void,
    rejectCallback?: () => void
  ): void;

  public show(
    audience: Audience,
    transfer: Transfer,
    availableItems: ShareAudience[],
    initialSelected: ShareAudience[] | null,
    acceptCallback: (request: UpdateSharedAudienceRequest) => void,
    rejectCallback?: () => void
  ): void;

  public show(
    audience: Audience,
    transfer: Transfer,
    arg3: TransferShareAudience | ShareAudience[],
    arg4:
      | ((request: UpdateSharedAudienceRequest) => void)
      | TransferShareAudience
      | ShareAudience[]
      | null,
    arg5?: ((request: UpdateSharedAudienceRequest) => void) | (() => void),
    arg6?: () => void
  ): void {
    const ref = this.modalService.open(TransfersShareDialogComponent, {
      windowClass: "transfer-share-modal",
      backdrop: "static",
      keyboard: false,
      scrollable: false
    });

    const dialog = ref.componentInstance as TransfersShareDialogComponent;
    dialog.audience = audience;
    dialog.transfer = transfer;

    let output: UpdateSharedAudienceRequest | null = null;

    // Flujo legacy: available flat items + selected flat items
    if (Array.isArray(arg3)) {
      const availableItems = arg3;
      const initialSelected = (arg4 as ShareAudience[] | null) ?? [];
      const acceptCallback = arg5 as (
        request: UpdateSharedAudienceRequest
      ) => void;
      const rejectCallback = arg6;

      dialog.init(
        this.fromLegacyItems(transfer, availableItems, initialSelected)
      );

      dialog.accept = (request: UpdateSharedAudienceRequest) => {
        output = request;
        ref.close("accept");
      };

      dialog.dismiss = () => {
        ref.dismiss("dismiss");
      };

      this.bindModalResult(ref, () => output, acceptCallback, rejectCallback);
      return;
    }

    // Flujo actual: available tree solamente
    if (typeof arg4 === "function") {
      const shareData = this.normalizeIncomingShareAudience(
        arg3 as IncomingTransferShareAudience,
        transfer
      );

      const acceptCallback = arg4 as (
        request: UpdateSharedAudienceRequest
      ) => void;
      const rejectCallback = arg5 as (() => void) | undefined;

      dialog.init(shareData);

      dialog.accept = (request: UpdateSharedAudienceRequest) => {
        output = request;
        ref.close("accept");
      };

      dialog.dismiss = () => {
        ref.dismiss("dismiss");
      };

      this.bindModalResult(ref, () => output, acceptCallback, rejectCallback);
      return;
    }

    // Flujo nuevo: available tree + shared tree/flat list
    const availableData = this.normalizeIncomingShareAudience(
      arg3 as IncomingTransferShareAudience,
      transfer
    );

    const mergedData = this.applySharedSelection(
      availableData,
      arg4 as TransferShareAudience | ShareAudience[] | null
    );

    const acceptCallback = arg5 as (
      request: UpdateSharedAudienceRequest
    ) => void;
    const rejectCallback = arg6;

    dialog.init(mergedData);

    dialog.accept = (request: UpdateSharedAudienceRequest) => {
      output = request;
      ref.close("accept");
    };

    dialog.dismiss = () => {
      ref.dismiss("dismiss");
    };

    this.bindModalResult(ref, () => output, acceptCallback, rejectCallback);
  }

  private bindModalResult(
    ref: NgbModalRef,
    getOutput: () => UpdateSharedAudienceRequest | null,
    acceptCallback: (request: UpdateSharedAudienceRequest) => void,
    rejectCallback?: () => void
  ): void {
    ref.result.then(
      (result) => {
        const output = getOutput();

        if (result === "accept" && output) {
          acceptCallback(output);
          return;
        }

        if (rejectCallback) {
          rejectCallback();
        }
      },
      () => {
        if (rejectCallback) {
          rejectCallback();
        }
      }
    );
  }

  private applySharedSelection(
    availableData: TransferShareAudience,
    sharedSource: TransferShareAudience | ShareAudience[] | null
  ): TransferShareAudience {
    const selectedKeys = this.extractSelectedLeafKeys(sharedSource);

    if (selectedKeys.size === 0) {
      return availableData;
    }

    return {
      ...availableData,
      platforms: (availableData.platforms || []).map((platform) => ({
        ...platform,
        businesses: (platform.businesses || []).map((business) =>
          this.applySelectedToBusiness(business, selectedKeys)
        ),
        groups: (platform.groups || []).map((group) =>
          this.applySelectedToGroup(group, selectedKeys)
        )
      }))
    };
  }

  private applySelectedToGroup(
    group: TransferShareSavedGroup,
    selectedKeys: Set<string>
  ): TransferShareSavedGroup {
    return {
      ...group,
      businesses: (group.businesses || []).map((business) =>
        this.applySelectedToBusiness(business, selectedKeys)
      )
    };
  }

  private applySelectedToBusiness(
    business: TransferShareBusiness,
    selectedKeys: Set<string>
  ): TransferShareBusiness {
    return {
      ...business,
      items: (business.items || []).map((item) => ({
        ...item,
        selected: selectedKeys.has(this.leafKey(item.type, item.id))
      }))
    };
  }

  private extractSelectedLeafKeys(
    source: TransferShareAudience | ShareAudience[] | null
  ): Set<string> {
    const keys = new Set<string>();

    if (!source) {
      return keys;
    }

    if (Array.isArray(source)) {
      for (const item of source) {
        if (item?.type === "advertiser" || item?.type === "account") {
          keys.add(this.leafKey(item.type, item.id));
        }
      }

      return keys;
    }

    for (const platform of source.platforms || []) {
      for (const business of platform.businesses || []) {
        for (const item of business.items || []) {
          keys.add(this.leafKey(item.type, item.id));
        }
      }

      for (const group of platform.groups || []) {
        for (const business of group.businesses || []) {
          for (const item of business.items || []) {
            keys.add(this.leafKey(item.type, item.id));
          }
        }
      }
    }

    return keys;
  }

  private leafKey(type: unknown, id: unknown): string {
    const normalizedType = type === "account" ? "account" : "advertiser";
    return `${normalizedType}:${String(id)}`;
  }

  private normalizeIncomingShareAudience(
    data: IncomingTransferShareAudience,
    transfer: Transfer
  ): TransferShareAudience {
    const resolvedTransferId =
      data?.transferId ??
      (transfer as any)?.id ??
      (transfer as any)?.transferId;

    const resolvedPlatformId = this.resolvePlatformId(
      transfer,
      data?.platformId
    );

    if (Array.isArray(data?.platforms) && data.platforms.length > 0) {
      return {
        transferId: resolvedTransferId,
        platformId: resolvedPlatformId,
        platforms: data.platforms.map((platform) =>
          this.normalizePlatform(platform, resolvedPlatformId)
        )
      };
    }

    return {
      transferId: resolvedTransferId,
      platformId: resolvedPlatformId,
      platforms: [
        {
          id: resolvedPlatformId,
          name: "",
          type: "platform",
          info: undefined,
          businesses: (data?.businesses || []).map((business) =>
            this.normalizeBusinessNode(business, resolvedPlatformId)
          ),
          groups: (data?.groups || []).map((group) =>
            this.normalizeSavedGroupNode(group, resolvedPlatformId)
          )
        }
      ]
    };
  }

  private fromLegacyItems(transfer: Transfer,availableItems: ShareAudience[],initialSelected: ShareAudience[]): TransferShareAudience {
    const items = availableItems ?? [];
    const selectedIds = new Set(
      (initialSelected ?? []).map((x) => String(x.id))
    );
    const selectedItemsByKey = new Map<string, ShareAudience>(
      (initialSelected ?? []).map((item) => [
        this.leafKey(item.type, item.id),
        item
      ])
    );
    const platforms = items.filter((x) => x.type === "platform");
    const savedGroups = items.filter((x) => x.type === "saved-group");
    const businesses = items.filter((x) => x.type === "business");
    const leafItems = items
      .filter((x) => x.type === "advertiser" || x.type === "account")
      .map((item) => {
        const selectedItem = selectedItemsByKey.get(
          this.leafKey(item.type, item.id)
        );

        return {
          ...item,

          audienceId:
            selectedItem?.audienceId ??
            item.audienceId,

          audienceName:
            selectedItem?.audienceName ??
            item.audienceName,

          isError:
            selectedItem?.isError ??
            item.isError ??
            false,

          errorMessage:
            selectedItem?.errorMessage ??
            item.errorMessage ??
            ""
        } as ShareAudience;
      }
    );

    const transferId =
      (transfer as any)?.id ?? (transfer as any)?.transferId;
    const platformId = this.resolvePlatformId(transfer);

    if (platforms.length > 0) {
      return {
        transferId,
        platformId,
        platforms: platforms.map((platform) =>
          this.mapPlatform(
            platform,
            savedGroups,
            businesses,
            leafItems,
            selectedIds
          )
        )
      };
    }

    const rootBusinesses = businesses.filter(
      (business) =>
        business.parentId == null ||
        String(business.parentId) === String(platformId)
    );

    const rootGroups = savedGroups.filter(
      (group) =>
        group.parentId == null ||
        String(group.parentId) === String(platformId)
    );

    return {
      transferId,
      platformId,
      platforms: [
        {
          id: platformId,
          name: "",
          type: "platform",
          info: undefined,
          businesses: rootBusinesses.map((business) =>
            this.mapBusiness(business, leafItems, selectedIds)
          ),
          groups: rootGroups.map((group) =>
            this.mapSavedGroup(group, businesses, leafItems, selectedIds)
          )
        }
      ]
    };
  }

  private mapPlatform(
    platform: ShareAudience,
    savedGroups: ShareAudience[],
    businesses: ShareAudience[],
    leafItems: ShareAudience[],
    selectedIds: Set<string>
  ): TransferSharePlatform {
    const platformId = String(platform.id);

    const directBusinesses = businesses.filter(
      (business) =>
        business.parentId != null && String(business.parentId) === platformId
    );

    const platformGroups = savedGroups.filter(
      (group) =>
        group.parentId != null && String(group.parentId) === platformId
    );

    return {
      id: platform.id,
      name: platform.name,
      type: "platform",
      info: platform.info,
      businesses: directBusinesses.map((business) =>
        this.mapBusiness(business, leafItems, selectedIds)
      ),
      groups: platformGroups.map((group) =>
        this.mapSavedGroup(group, businesses, leafItems, selectedIds)
      )
    };
  }

  private mapSavedGroup(
    group: ShareAudience,
    businesses: ShareAudience[],
    leafItems: ShareAudience[],
    selectedIds: Set<string>
  ): TransferShareSavedGroup {
    const groupId = String(group.id);

    const groupBusinesses = businesses.filter(
      (business) =>
        business.parentId != null && String(business.parentId) === groupId
    );

    return {
      id: group.id,
      name: group.name,
      type: "saved-group",
      parentId: group.parentId,
      info: group.info,
      businesses: groupBusinesses.map((business) =>
        this.mapBusiness(business, leafItems, selectedIds)
      )
    };
  }

  private mapBusiness(
    business: ShareAudience,
    leafItems: ShareAudience[],
    selectedIds: Set<string>
  ): TransferShareBusiness {
    const businessId = String(business.id);

    const businessItems = leafItems.filter(
      (item) =>
        item.parentId != null && String(item.parentId) === businessId
    );

    return {
      id: business.id,
      name: business.name,
      type: "business",
      parentId: business.parentId,
      info: business.info,
      items: businessItems.map((item) =>
        this.mapLeafItem(item, business.id, selectedIds)
      )
    };
  }

  private mapLeafItem(item: ShareAudience,fallbackParentId: string | number,selectedIds: Set<string>): TransferShareItem {
    return {
      id: item.id,
      name: item.name,
      type: item.type === "account" ? "account" : "advertiser",
      parentId: item.parentId ?? fallbackParentId,
      businessId: item.businessId,
      businessName: item.businessName,
      audienceId: item.audienceId,
      audienceName: item.audienceName,
      info: item.info,
      selected: selectedIds.has(String(item.id))
    };
  }

  private normalizePlatform(
    platform: Partial<TransferSharePlatform>,
    fallbackPlatformId?: string | number
  ): TransferSharePlatform {
    const platformId =
      platform.id !== null && platform.id !== undefined
        ? platform.id
        : fallbackPlatformId;

    return {
      id: platformId as string | number,
      name: platform.name || "",
      type: "platform",
      info: platform.info,
      businesses: (platform.businesses || []).map((business) =>
        this.normalizeBusinessNode(business, platformId)
      ),
      groups: (platform.groups || []).map((group) =>
        this.normalizeSavedGroupNode(group, platformId)
      )
    };
  }

  private normalizeSavedGroupNode(
    group: Partial<TransferShareSavedGroup>,
    fallbackParentId?: string | number
  ): TransferShareSavedGroup {
    return {
      id: group.id as string | number,
      name: group.name || "",
      type: "saved-group",
      parentId:
        group.parentId !== null && group.parentId !== undefined
          ? group.parentId
          : fallbackParentId,
      info: group.info,
      businesses: (group.businesses || []).map((business) =>
        this.normalizeBusinessNode(business, group.id)
      )
    };
  }

  private normalizeBusinessNode(
    business: Partial<TransferShareBusiness>,
    fallbackParentId?: string | number
  ): TransferShareBusiness {
    return {
      id: business.id as string | number,
      name: business.name || "",
      type: "business",
      parentId:
        business.parentId !== null && business.parentId !== undefined
          ? business.parentId
          : fallbackParentId,
      info: business.info,
      items: (business.items || []).map((item) =>
        this.normalizeLeafItemNode(item, business.id)
      )
    };
  }

  private normalizeLeafItemNode(
    item: Partial<TransferShareItem>,
    fallbackParentId?: string | number
  ): TransferShareItem {
    return {
      id: item.id as string | number,
      name: item.name || "",
      type: item.type === "account" ? "account" : "advertiser",
      parentId:
        item.parentId !== null && item.parentId !== undefined
          ? item.parentId
          : fallbackParentId,
      info: item.info,
      selected: item.selected === true
    };
  }

  private resolvePlatformId(
    transfer: Transfer,
    explicitPlatformId?: string | number
  ): string | number {
    if (
      explicitPlatformId !== null &&
      explicitPlatformId !== undefined &&
      explicitPlatformId !== ""
    ) {
      return explicitPlatformId;
    }

    if (
      (transfer as any)?.targetPlatformId !== null &&
      (transfer as any)?.targetPlatformId !== undefined
    ) {
      return (transfer as any).targetPlatformId;
    }

    if (
      (transfer as any)?.targetPlatform !== null &&
      (transfer as any)?.targetPlatform !== undefined
    ) {
      return (transfer as any).targetPlatform;
    }

    return "platform-root";
  }
}